import { useQuery } from '@tanstack/react-query'
import {
  CARD_MANAGEMENT_VIEW_ACTION,
  hasAvailableCapability,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { physicalCardManagementQueries } from 'src/queries/physicalCardManagement/physicalCardManagement.queries'

// True only when the server says this specific card may see the Card Management
// demo UI. This single boolean is the gate for the whole demo surface: every
// other card-management action sits behind VIEW_PHYSICAL_CARD_MANAGEMENT, so if
// this is false there is nothing to show.
//
// Composed rather than a bare single-query wrapper: it owns the fail-closed
// derivation, so an error or absent response (e.g. a 403 for a non-member)
// resolves to false instead of an error state every call site must interpret.
export const useCanSeeCardManagementDemo = (
  organizationId: string | undefined,
  creditCardId: string | undefined,
  enabled = true,
): boolean => {
  const capabilitiesQuery = physicalCardManagementQueries.capabilities(
    organizationId,
    creditCardId,
  )
  const { data, isSuccess } = useQuery({
    ...capabilitiesQuery,
    // The factory already guards on both ids; AND the caller's gate on top so a
    // consumer that has another reason to show the UI (e.g. a remote-config flag
    // that already forces it on) can skip this request entirely. Hooks can't be
    // called conditionally — the query's `enabled` is how we opt out.
    enabled: capabilitiesQuery.enabled && enabled,
  })

  // Trust `data` only while the latest fetch is a success. React Query retains
  // the last successful response after a refetch errors (and while a disabled
  // query sits pending), so reading `data` unconditionally would keep the demo
  // open on a transient error or a now-403 kill-switch — the opposite of
  // fail-closed. On any non-success status the gate is closed.
  if (!isSuccess) {
    return false
  }

  // `?? []` is belt-and-suspenders: the type says `capabilities` is always an
  // array, but there is no runtime schema validation, and a malformed body must
  // fail closed here — never throw `.some()` on undefined mid-render.
  return hasAvailableCapability(
    data.capabilities ?? [],
    CARD_MANAGEMENT_VIEW_ACTION,
  )
}
